import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Copy, FileEdit, Loader2 } from "lucide-react";
import { API, DOCUMENT_TEMPLATES } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const DocumentsPage = () => {
  const [template, setTemplate] = useState("demand_letter");
  const [form, setForm] = useState({
    case_type: "personal_injury",
    claimant_name: "",
    adverse_party: "",
    incident_date: "",
    incident_description: "",
    damages_description: "",
    medical_expenses: "",
    lost_wages: "",
    demand_amount: ""
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/documents/generate`, {
        template_type: template,
        case_details: form
      });
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(result?.content || '');
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="documents-page">
      <Disclaimer />
      <div className="container-shell py-8">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Document Merge</h1>
          <p className="text-muted-foreground">Generate legal documents from templates</p>
        </div>
        
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader><CardTitle>Template</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {DOCUMENT_TEMPLATES.map(t => (
                  <button
                    key={t.value}
                    onClick={() => setTemplate(t.value)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                      template === t.value ? 'bg-primary/10 text-primary' : 'hover:bg-slate-100'
                    }`}
                  >
                    <t.icon className="h-5 w-5" />
                    <span className="font-medium">{t.label}</span>
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>
          
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader><CardTitle>Case Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Claimant Name</Label>
                    <Input value={form.claimant_name} onChange={e => setForm(p => ({ ...p, claimant_name: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Adverse Party</Label>
                    <Input value={form.adverse_party} onChange={e => setForm(p => ({ ...p, adverse_party: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Incident Date</Label>
                    <Input type="date" value={form.incident_date} onChange={e => setForm(p => ({ ...p, incident_date: e.target.value }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Demand Amount</Label>
                    <Input value={form.demand_amount} onChange={e => setForm(p => ({ ...p, demand_amount: e.target.value }))} placeholder="$50,000" />
                  </div>
                </div>
                
                <div className="space-y-1.5">
                  <Label>Incident Description</Label>
                  <Textarea value={form.incident_description} onChange={e => setForm(p => ({ ...p, incident_description: e.target.value }))} rows={3} />
                </div>
                
                <div className="space-y-1.5">
                  <Label>Damages Description</Label>
                  <Textarea value={form.damages_description} onChange={e => setForm(p => ({ ...p, damages_description: e.target.value }))} rows={3} />
                </div>
                
                <Button onClick={generate} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileEdit className="h-4 w-4 mr-2" />}
                  Generate Document
                </Button>
              </CardContent>
            </Card>
            
            {result && (
              <Card className="animate-fade-in">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Generated Document</CardTitle>
                  <Button variant="outline" size="sm" onClick={copyToClipboard}>
                    <Copy className="h-4 w-4 mr-1" /> Copy
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="document-preview max-h-[500px] overflow-auto">
                    {result.content}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
};


export default DocumentsPage;
